import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Breadcrumb,
  Tag,
  Space,
  Modal,
  Card,
  Row,
  Col,
  Input,
  Statistic,
  Spin,
  Descriptions,
  Tabs,
  Tooltip,
} from 'antd';
import {
  HomeOutlined,
  EyeOutlined,
  SearchOutlined,
  InboxOutlined,
  UserOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showError } from '../../../../components/errorHandler';
import { calculateCFT } from '../../../../utils/cftCalculator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

interface WoodItem {
  wood_id: number;
  wood_name: string;
  wood_length: string | number;
  wood_thickness: string | number;
  wood_status: number;
}

interface WoodTypeItem {
  wood_id: number;
  wood_name: string;
  wood_status: number;
}

interface IssuedTimberRecord {
  issue_id: number;
  issue_to: string;
  date: string;
  pi_no: string;
  wo_no: string;
  wo_id?: number;
  product_name: string;
  cft: number;
  is_completed: boolean;
  stage_status: string;
}

interface TimberInventoryRow {
  key: string;
  wood_id: number;
  wood_type_id: number;
  wood_name: string;
  wood_type_name: string;
  wood_dimension: string;
  total_purchased_cft: number;
  total_issued_cft: number;
  store_available_cft: number;
  at_employee_cft: number;
  consumed_cft: number;
  actual_stock_cft: number;
  available_cft: number;
  avg_rate: number;
  total_amount: number;
  purchase_bills: any[];
  issued_records: IssuedTimberRecord[];
  at_employee_records: IssuedTimberRecord[];
  completed_records: IssuedTimberRecord[];
  work_orders: any[];
}

const TimberInventory: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [woods, setWoods] = useState<WoodItem[]>([]);
  const [woodTypes, setWoodTypes] = useState<WoodTypeItem[]>([]);
  const [poBills, setPoBills] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // View Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<TimberInventoryRow | null>(null);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [woodRes, woodTypeRes, poRes, issueRes, woRes, empRes] = await Promise.all([
        apiRequest<WoodItem[]>({ method: 'get', url: '/woods' }).catch(() => []),
        apiRequest<WoodTypeItem[]>({ method: 'get', url: '/wood-types' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/get-po-bill' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/issue-master?type=Timber' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/get-work-orders' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/employees' }).catch(() => []),
      ]);

      setWoods(woodRes || []);
      setWoodTypes(woodTypeRes || []);
      setPoBills(poRes || []);
      setIssues(issueRes || []);
      setWorkOrders(woRes || []);
      setEmployees(empRes || []);
    } catch (err) {
      console.error(err);
      showError('Failed to load timber inventory data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Compute timber inventory grouping by (wood_id, wood_type_id)
  const inventoryData = useMemo(() => {
    const rowsMap = new Map<string, TimberInventoryRow>();

    // Helper: Initialize empty row
    const getOrCreateRow = (woodId: number, woodTypeId: number): TimberInventoryRow => {
      const targetWoodId = Number(woodId || 0);
      const targetWoodTypeId = Number(woodTypeId || 0);

      const groupKey = `${targetWoodId}_${targetWoodTypeId}`;
      if (rowsMap.has(groupKey)) {
        return rowsMap.get(groupKey)!;
      }

      // Fallback matching: If exact groupKey not found, check if a row for this woodId already exists
      if (targetWoodId > 0) {
        for (const [key, existingRow] of rowsMap.entries()) {
          if (Number(existingRow.wood_id) === targetWoodId) {
            if (!targetWoodTypeId || targetWoodTypeId <= 1 || Number(existingRow.wood_type_id) === targetWoodTypeId) {
              return existingRow;
            }
          }
        }
      }

      const woodObj = woods.find((w) => Number(w.wood_id) === targetWoodId);
      const woodTypeObj = woodTypes.find((wt) => Number(wt.wood_id) === targetWoodTypeId);

      const woodDimension = woodObj ? `${woodObj.wood_length}' x ${woodObj.wood_thickness}"` : 'Standard Dimension';
      const woodName = woodObj?.wood_name || 'Timber Wood';
      const woodTypeName = woodTypeObj?.wood_name || 'Standard';

      const newRow: TimberInventoryRow = {
        key: groupKey,
        wood_id: targetWoodId,
        wood_type_id: targetWoodTypeId,
        wood_name: woodName,
        wood_type_name: woodTypeName,
        wood_dimension: woodDimension,
        total_purchased_cft: 0,
        total_issued_cft: 0,
        store_available_cft: 0,
        at_employee_cft: 0,
        consumed_cft: 0,
        actual_stock_cft: 0,
        available_cft: 0,
        avg_rate: 0,
        total_amount: 0,
        purchase_bills: [],
        issued_records: [],
        at_employee_records: [],
        completed_records: [],
        work_orders: [],
      };

      rowsMap.set(groupKey, newRow);
      return newRow;
    };

    // 1. Process all Purchase Bills for Timber
    poBills.forEach((bill: any) => {
      const details = bill.po_details || [];
      details.forEach((item: any) => {
        const woodId = Number(item.wood_id || item.pbg_wood_id || 0);
        const woodTypeId = Number(item.wood_type_id || item.pbg_wood_type || item.pbg_wood_type_id || 0);
        const cft = Number(item.pbg_cft || item.pbg_qty || 0);
        const rate = Number(item.pbg_rate || item.pbg_price || 0);

        if (!woodId && !woodTypeId && cft <= 0) return;

        const row = getOrCreateRow(woodId, woodTypeId);
        row.total_purchased_cft += cft;
        const pbgIssued = Number(item.pbg_issued_cft || 0);
        const pbgAvailable = Math.max(0, cft - pbgIssued);

        row.purchase_bills.push({
          bill_id: bill.pbill_id,
          bill_no: bill.pbill_bill_no || `BILL-${bill.pbill_id}`,
          po_no: bill.pbill_po_no || '-',
          date: bill.pbill_date || bill.pbill_created_on,
          cft: cft,
          issued_cft: pbgIssued,
          available_cft: pbgAvailable,
          rate: rate,
          amount: cft * rate,
          available_amount: pbgAvailable * rate,
        });
      });
    });

    // 2. Process all Timber Issues
    // Logic: When issued from timber to machinery (via WO):
    // Till not completed in machinery -> show at employee
    // Complete in machinery -> remove from employee and remove from actual stock
    issues.forEach((issue: any) => {
      const itemsList = Array.isArray(issue.items) ? issue.items : [];
      const rawWoNo = issue.im_master_order_no;
      const rawWoId = issue.work_order_id;
      const rawPiNo = issue.im_master_pi_no;

      // Match Work Order
      let matchedWo = workOrders.find((wo: any) => {
        if (rawWoId && Number(wo.wo_id) === Number(rawWoId)) return true;
        if (rawWoNo && (String(wo.wo_id) === String(rawWoNo) || String(wo.wo_number) === String(rawWoNo))) return true;
        if (issue.work_order && (String(wo.wo_id) === String(issue.work_order.wo_id) || String(wo.wo_number) === String(issue.work_order.work_order_no))) return true;
        return false;
      });

      if (!matchedWo && rawPiNo) {
        matchedWo = workOrders.find((wo: any) =>
          String(wo.pi_order_no) === String(rawPiNo) || String(wo.wo_pi_id) === String(rawPiNo)
        );
      }

      // Check if work order is completed in machinery
      const isCompleted = matchedWo
        ? (String(matchedWo.wo_status).toLowerCase() === 'completed' ||
          matchedWo.isCompleted === true ||
          (Number(matchedWo.wo_qty) > 0 && Number(matchedWo.total_complete || 0) >= Number(matchedWo.wo_qty)))
        : (issue.im_status === 2 || String(issue.im_status).toLowerCase() === 'completed');

      // Resolve Employee Name
      let employeeName = '';
      if (issue.employee_name && typeof issue.employee_name === 'string' && issue.employee_name.trim()) {
        employeeName = issue.employee_name.trim();
      } else {
        const empId = Number(issue.im_issue_to || issue.employee_id || matchedWo?.wo_employee_id);
        if (empId) {
          const emp = employees.find((e) => Number(e.employee_id) === empId);
          if (emp) employeeName = `${emp.employee_fname || emp.fname || ''} ${emp.employee_lname || emp.lname || ''}`.trim();
        }
        if (!employeeName && matchedWo?.employee_fname) {
          employeeName = `${matchedWo.employee_fname} ${matchedWo.employee_lname || ''}`.trim();
        }
      }
      if (!employeeName) {
        if (issue.im_issue_to && isNaN(Number(issue.im_issue_to))) {
          employeeName = String(issue.im_issue_to);
        } else {
          employeeName = 'Assigned Member';
        }
      }

      const issueDate = issue.im_created_on || issue.created_at;
      const piNo = issue.im_master_pi_no || (matchedWo?.pi_order_no || '-');
      const woNo = issue.im_master_order_no || (matchedWo?.wo_number || (matchedWo ? `WO-${matchedWo.wo_id}` : '-'));
      const productName = matchedWo?.product_name || issue.product_name || '-';

      itemsList.forEach((it: any) => {
        const woodId = Number(it.woodId || it.wood_id || it.tiod_wood_id || it.imti_wood_id || 0);
        const woodTypeId = Number(it.woodTypeId || it.wood_type_id || it.tiod_wood_type_id || it.imti_wood_type_id || 0);

        const woodObj = woods.find((w) => Number(w.wood_id) === woodId);

        let issuedCft = Number(it.cft || it.tiod_cft || it.imti_cft || 0);
        if (issuedCft <= 0 && it.widths && typeof it.widths === 'object') {
          const widthQtyTotal = Object.values(it.widths).reduce((acc: number, val: any) => acc + Number(val || 0), 0);
          if (woodObj) {
            issuedCft = calculateCFT(widthQtyTotal, Number(woodObj.wood_length), Number(woodObj.wood_thickness));
          }
        } else if (issuedCft <= 0 && it.qty) {
          const length = Number(woodObj?.wood_length || 0);
          const thickness = Number(woodObj?.wood_thickness || 0);
          if (length && thickness) {
            issuedCft = calculateCFT(Number(it.qty || it.issued_pcs), length, thickness);
          }
        }

        const row = getOrCreateRow(woodId, woodTypeId);
        row.total_issued_cft += issuedCft;

        const record: IssuedTimberRecord = {
          issue_id: issue.im_id,
          issue_to: employeeName,
          date: issueDate,
          pi_no: piNo,
          wo_no: woNo,
          wo_id: matchedWo?.wo_id,
          product_name: productName,
          cft: parseFloat(issuedCft.toFixed(4)),
          is_completed: isCompleted,
          stage_status: isCompleted ? 'Completed in Machinery' : 'At Employee (In Machinery)',
        };

        if (isCompleted) {
          row.consumed_cft += issuedCft;
          row.completed_records.push(record);
        } else {
          row.at_employee_cft += issuedCft;
          row.at_employee_records.push(record);
        }

        row.issued_records.push(record);
      });
    });

    // 3. Ensure master woods are present in list even if not yet purchased
    woods.forEach((w) => {
      woodTypes.forEach((wt) => {
        getOrCreateRow(w.wood_id, wt.wood_id);
      });
    });

    // 4. Compute Weighted Average Price & Final Stocks
    const list: TimberInventoryRow[] = [];
    rowsMap.forEach((row) => {
      const pendingBills = row.purchase_bills.filter((b) => (b.available_cft ?? b.cft) > 0);
      let avgRate = 0;

      if (pendingBills.length > 0) {
        const pendingCft = pendingBills.reduce((sum, b) => sum + (b.available_cft ?? b.cft), 0);
        const pendingAmount = pendingBills.reduce((sum, b) => sum + (b.available_cft ?? b.cft) * b.rate, 0);
        avgRate = pendingCft > 0 ? pendingAmount / pendingCft : 0;
      }

      if (avgRate === 0 && row.purchase_bills.length > 0) {
        const totalCost = row.purchase_bills.reduce((sum, b) => sum + b.amount, 0);
        const totalCft = row.purchase_bills.reduce((sum, b) => sum + b.cft, 0);
        avgRate = totalCft > 0 ? totalCost / totalCft : (row.purchase_bills[0]?.rate || 0);
      }

      // Store Available CFT: Unissued raw stock in store/yard
      const storeAvailable = Math.max(0, row.total_purchased_cft - row.total_issued_cft);

      // Actual Stock:
      // While not completed in machinery, timber shows at employee (and is part of actual stock on premises).
      // Once completed, it is removed from employee and removed from actual stock.
      const actualStock = Math.max(0, storeAvailable + row.at_employee_cft);
      const totalAmount = actualStock * avgRate;

      // Match Work Orders that relate to issued PIs
      const relevantPiNos = new Set(
        row.issued_records
          .map((r) => String(r.pi_no).toLowerCase())
          .filter((p) => p && p !== '-')
      );
      const matchedWOs = workOrders.filter((wo) => {
        const piOrder = String(wo.pi_order_no || '').toLowerCase();
        return relevantPiNos.has(piOrder);
      });

      list.push({
        ...row,
        total_purchased_cft: parseFloat(row.total_purchased_cft.toFixed(4)),
        total_issued_cft: parseFloat(row.total_issued_cft.toFixed(4)),
        store_available_cft: parseFloat(storeAvailable.toFixed(4)),
        at_employee_cft: parseFloat(row.at_employee_cft.toFixed(4)),
        consumed_cft: parseFloat(row.consumed_cft.toFixed(4)),
        actual_stock_cft: parseFloat(actualStock.toFixed(4)),
        available_cft: parseFloat(actualStock.toFixed(4)), // For compatibility
        avg_rate: parseFloat(avgRate.toFixed(4)),
        total_amount: parseFloat(totalAmount.toFixed(4)),
        work_orders: matchedWOs,
      });
    });

    // Sort by actual stock CFT descending
    return list.sort((a, b) => b.actual_stock_cft - a.actual_stock_cft);
  }, [woods, woodTypes, poBills, issues, workOrders, employees]);

  // Filtered rows by search query
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return inventoryData;
    const q = searchQuery.toLowerCase();
    return inventoryData.filter(
      (r) =>
        r.wood_name.toLowerCase().includes(q) ||
        r.wood_type_name.toLowerCase().includes(q) ||
        r.wood_dimension.toLowerCase().includes(q)
    );
  }, [inventoryData, searchQuery]);

  // Summary statistics
  const totalActualStockCft = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.actual_stock_cft, 0);
  }, [inventoryData]);

  const totalStoreAvailableCft = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.store_available_cft, 0);
  }, [inventoryData]);

  const totalAtEmployeeCft = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.at_employee_cft, 0);
  }, [inventoryData]);

  const totalConsumedCft = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.consumed_cft, 0);
  }, [inventoryData]);

  const totalInventoryValue = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.total_amount, 0);
  }, [inventoryData]);

  // Export Excel handler
  const handleExportExcel = () => {
    const data = filteredRows.map((r, idx) => ({
      'Sr No.': idx + 1,
      'Wood Name / Dimension': `${r.wood_name} (${r.wood_dimension})`,
      'Wood Type': r.wood_type_name,
      'Purchased CFT': r.total_purchased_cft,
      'Store Available (Yard) CFT': r.store_available_cft,
      'At Employee (In Machinery) CFT': r.at_employee_cft,
      'Consumed (Completed WO) CFT': r.consumed_cft,
      'Actual Stock CFT': r.actual_stock_cft,
      'Avg Rate (₹/CFT Excl. GST)': r.avg_rate,
      'Total Amount (₹ Excl. GST)': r.total_amount,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timber Inventory');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Timber_Inventory_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`
    );
  };

  const handleOpenViewModal = (record: TimberInventoryRow) => {
    setSelectedRow(record);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: 'Sr.',
      key: 'sr',
      width: 50,
      align: 'center' as const,
      render: (_: any, __: any, idx: number) => idx + 1,
    },
    {
      title: 'Wood Name',
      key: 'wood_name',
      render: (_: any, record: TimberInventoryRow) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{record.wood_name}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{record.wood_dimension}</div>
        </div>
      ),
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.wood_name.localeCompare(b.wood_name),
    },
    {
      title: 'Wood Type',
      dataIndex: 'wood_type_name',
      key: 'wood_type_name',
      width: 120,
      render: (type: string) => (
        <Tag color="blue" style={{ fontWeight: 600, borderRadius: 12, padding: '2px 8px' }}>
          {type}
        </Tag>
      ),
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.wood_type_name.localeCompare(b.wood_type_name),
    },
    {
      title: 'Store Stock (Yard)',
      dataIndex: 'store_available_cft',
      key: 'store_available_cft',
      width: 140,
      align: 'right' as const,
      render: (cft: number) => (
        <span style={{ fontSize: 14, fontWeight: 700, color: cft > 0 ? '#0284c7' : '#94a3b8' }}>
          {cft.toFixed(4)} CFT
        </span>
      ),
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.store_available_cft - b.store_available_cft,
    },
    {
      title: 'At Employee (In Machinery)',
      dataIndex: 'at_employee_cft',
      key: 'at_employee_cft',
      width: 170,
      align: 'center' as const,
      render: (cft: number, record: TimberInventoryRow) => {
        if (cft <= 0) return <span style={{ color: '#94a3b8' }}>0.00 CFT</span>;
        const employeeNames = Array.from(new Set(record.at_employee_records.map((r) => r.issue_to))).join(', ');
        return (
          <Tooltip title={`With: ${employeeNames || 'Assigned workers'} (till WO complete)`}>
            <Tag color="orange" style={{ fontWeight: 800, fontSize: 12, padding: '2px 8px' }}>
              <ClockCircleOutlined /> {cft.toFixed(4)} CFT
            </Tag>
            <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>
              {record.at_employee_records.length} active holding{record.at_employee_records.length > 1 ? 's' : ''}
            </div>
          </Tooltip>
        );
      },
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.at_employee_cft - b.at_employee_cft,
    },
    {
      title: 'Completed (Consumed)',
      dataIndex: 'consumed_cft',
      key: 'consumed_cft',
      width: 160,
      align: 'center' as const,
      render: (cft: number) => {
        if (cft <= 0) return <span style={{ color: '#94a3b8' }}>0.00 CFT</span>;
        return (
          <div>
            <Tag color="green" style={{ fontWeight: 700, fontSize: 12 }}>
              <CheckCircleOutlined /> {cft.toFixed(4)} CFT
            </Tag>
            <div style={{ fontSize: 10, color: '#16a34a', marginTop: 2 }}>Removed from stock</div>
          </div>
        );
      },
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.consumed_cft - b.consumed_cft,
    },
    {
      title: 'Actual Stock CFT',
      dataIndex: 'actual_stock_cft',
      key: 'actual_stock_cft',
      width: 160,
      render: (cft: number, record: TimberInventoryRow) => (
        <div>
          <span style={{ fontSize: 15, fontWeight: 800, color: cft > 0 ? '#10b981' : '#94a3b8' }}>
            {cft.toFixed(4)} CFT
          </span>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            Store: {record.store_available_cft} | At Emp: {record.at_employee_cft}
          </div>
        </div>
      ),
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.actual_stock_cft - b.actual_stock_cft,
    },
    {
      title: 'Avg Rate (₹/CFT)',
      dataIndex: 'avg_rate',
      key: 'avg_rate',
      width: 130,
      align: 'right' as const,
      render: (rate: number) => (
        <span style={{ fontWeight: 600, color: '#2563eb' }}>
          ₹ {rate.toFixed(4)}
        </span>
      ),
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.avg_rate - b.avg_rate,
    },
    {
      title: 'Total Value (Excl. GST)',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 160,
      align: 'right' as const,
      render: (amt: number) => (
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
          ₹ {amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      sorter: (a: TimberInventoryRow, b: TimberInventoryRow) => a.total_amount - b.total_amount,
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      align: 'center' as const,
      render: (_: any, record: TimberInventoryRow) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleOpenViewModal(record)}
          style={{ borderRadius: 6, fontWeight: 600 }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px 20px', minHeight: '90vh', backgroundColor: '#f8fafc' }}>
      {/* ── Breadcrumb & Title ── */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/inventory-managements">Inventory</Breadcrumb.Item>
        <Breadcrumb.Item>Timber Inventory</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: '#0f172a' }}>
            🪵 Timber Inventory & Stock Management
          </h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
            Live stock tracking: Issued timber stays at employee till machinery completion, and is removed from employee & actual stock upon completion.
          </p>
        </div>

        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAllData}
            loading={loading}
            style={{ borderRadius: 6, fontWeight: 600 }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            style={{ borderRadius: 6, fontWeight: 600, backgroundColor: '#107c41', borderColor: '#107c41' }}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      {/* ── Metrics Cards Banner ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Card 1: Actual Timber Stock */}
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>ACTUAL TIMBER STOCK</span>}
              value={totalActualStockCft}
              precision={2}
              suffix="CFT"
              valueStyle={{ color: '#10b981', fontWeight: 800 }}
              prefix={<InboxOutlined />}
            />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Store: <strong>{totalStoreAvailableCft.toFixed(4)}</strong> | At Emp: <strong>{totalAtEmployeeCft.toFixed(4)}</strong> CFT
            </div>
          </Card>
        </Col>

        {/* Card 2: At Employee (In Machinery) */}
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>AT EMPLOYEE (IN MACHINERY)</span>}
              value={totalAtEmployeeCft}
              precision={2}
              suffix="CFT"
              valueStyle={{ color: '#fa8c16', fontWeight: 800 }}
              prefix={<UserOutlined />}
            />
            <div style={{ fontSize: 11, color: '#d97706', marginTop: 4 }}>
              Holding with workers till WO completes
            </div>
          </Card>
        </Col>

        {/* Card 3: Consumed (Completed in Machinery) */}
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>CONSUMED (COMPLETED WO)</span>}
              value={totalConsumedCft}
              precision={2}
              suffix="CFT"
              valueStyle={{ color: '#059669', fontWeight: 800 }}
              prefix={<CheckCircleOutlined />}
            />
            <div style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>
              Removed from employee & actual stock
            </div>
          </Card>
        </Col>

        {/* Card 4: Total Value */}
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>TOTAL VALUE (EXCL. GST)</span>}
              value={totalInventoryValue}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#2563eb', fontWeight: 800 }}
            />
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Value of actual stock on hand
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Search & Filter Console ── */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Input
          size="large"
          placeholder="Search by wood name, dimension, or wood type..."
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{ maxWidth: 400, borderRadius: 8 }}
        />
      </Card>

      {/* ── Main Inventory Table ── */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={filteredRows}
            columns={columns}
            rowKey="key"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total}`,
            }}
            bordered
            size="middle"
          />
        )}
      </div>

      {/* ── Location & Tracking Modal ── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 800 }}>
              🪵 Product Exact Location & Live Tracking
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={950}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedRow && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 12 }}>
            {/* Header Summary */}
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }}>
              <Descriptions.Item label="Wood Name">
                <strong>{selectedRow.wood_name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Dimension">
                {selectedRow.wood_dimension}
              </Descriptions.Item>
              <Descriptions.Item label="Wood Type">
                <Tag color="blue">{selectedRow.wood_type_name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Actual Stock">
                <span style={{ fontWeight: 800, color: '#10b981', fontSize: 15 }}>
                  {selectedRow.actual_stock_cft} CFT
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Store Available (Yard)">
                <strong style={{ color: '#0284c7' }}>{selectedRow.store_available_cft} CFT</strong>
              </Descriptions.Item>
              <Descriptions.Item label="At Employee (Machinery)">
                <strong style={{ color: '#fa8c16' }}>{selectedRow.at_employee_cft} CFT</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Consumed (WO Complete)">
                <strong style={{ color: '#059669' }}>{selectedRow.consumed_cft} CFT</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Avg Purchase Price">
                <strong style={{ color: '#2563eb' }}>₹ {selectedRow.avg_rate} / CFT</strong>
              </Descriptions.Item>
            </Descriptions>


            {/* Tabs for At Employee, Completed Consumption, and Purchase Bills */}
            <Tabs
              defaultActiveKey={selectedRow.at_employee_records.length > 0 ? 'at_employee' : 'purchase_bills'}
              items={[
                {
                  key: 'at_employee',
                  label: (
                    <span>
                      <UserOutlined /> At Employee (Till Complete) ({selectedRow.at_employee_records.length})
                    </span>
                  ),
                  children: (
                    <Table
                      style={{ marginTop: 8 }}
                      size="small"
                      bordered
                      pagination={false}
                      dataSource={selectedRow.at_employee_records}
                      rowKey={(r, i) => `${r.issue_id}_${i}`}
                      columns={[
                        {
                          title: 'Assigned Employee',
                          dataIndex: 'issue_to',
                          key: 'issue_to',
                          render: (name) => <strong style={{ color: '#0f172a' }}>{name}</strong>,
                        },
                        {
                          title: 'Holding CFT',
                          dataIndex: 'cft',
                          key: 'cft',
                          render: (cft) => <span style={{ color: '#fa8c16', fontWeight: 700 }}>{cft} CFT</span>,
                        },
                        {
                          title: 'Ref Work Order / PI',
                          key: 'ref',
                          render: (_, r) => `WO #${r.wo_no} (PI #${r.pi_no})`,
                        },
                        {
                          title: 'Product Name',
                          dataIndex: 'product_name',
                          key: 'product_name',
                        },
                        {
                          title: 'Machinery Status',
                          key: 'status',
                          render: () => (
                            <Tag color="orange" style={{ fontWeight: 700 }}>
                              At Employee (In Machinery)
                            </Tag>
                          ),
                        },
                        {
                          title: 'Issue Date',
                          dataIndex: 'date',
                          key: 'date',
                          render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '-'),
                        },
                      ]}
                      locale={{ emptyText: 'No timber currently held at employee for active work orders' }}
                    />
                  ),
                },
                {
                  key: 'completed',
                  label: (
                    <span>
                      <CheckCircleOutlined /> Completed & Removed ({selectedRow.completed_records.length})
                    </span>
                  ),
                  children: (
                    <Table
                      style={{ marginTop: 8 }}
                      size="small"
                      bordered
                      pagination={false}
                      dataSource={selectedRow.completed_records}
                      rowKey={(r, i) => `${r.issue_id}_${i}`}
                      columns={[
                        {
                          title: 'Employee Name',
                          dataIndex: 'issue_to',
                          key: 'issue_to',
                          render: (name) => <strong>{name}</strong>,
                        },
                        {
                          title: 'Consumed CFT',
                          dataIndex: 'cft',
                          key: 'cft',
                          render: (cft) => <span style={{ color: '#059669', fontWeight: 700 }}>{cft} CFT</span>,
                        },
                        {
                          title: 'Ref Work Order / PI',
                          key: 'ref',
                          render: (_, r) => `WO #${r.wo_no} (PI #${r.pi_no})`,
                        },
                        {
                          title: 'Product Name',
                          dataIndex: 'product_name',
                          key: 'product_name',
                        },
                        {
                          title: 'Status',
                          key: 'status',
                          render: () => (
                            <Tag color="green" style={{ fontWeight: 700 }}>
                              Completed (Removed from Stock)
                            </Tag>
                          ),
                        },
                        {
                          title: 'Issue Date',
                          dataIndex: 'date',
                          key: 'date',
                          render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '-'),
                        },
                      ]}
                      locale={{ emptyText: 'No completed timber consumption records yet' }}
                    />
                  ),
                },
                {
                  key: 'purchase_bills',
                  label: (
                    <span>
                      <InboxOutlined /> Purchase Bills History ({selectedRow.purchase_bills.length})
                    </span>
                  ),
                  children: (
                    <Table
                      style={{ marginTop: 8 }}
                      size="small"
                      bordered
                      pagination={false}
                      dataSource={selectedRow.purchase_bills}
                      rowKey={(r, i) => `${r.bill_id}_${i}`}
                      columns={[
                        {
                          title: 'Bill No.',
                          dataIndex: 'bill_no',
                          key: 'bill_no',
                          render: (no) => <strong>{no}</strong>,
                        },
                        {
                          title: 'Date',
                          dataIndex: 'date',
                          key: 'date',
                          render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '-'),
                        },
                        {
                          title: 'Purchased CFT',
                          dataIndex: 'cft',
                          key: 'cft',
                          render: (cft) => `${cft} CFT`,
                        },
                        {
                          title: 'Rate (₹/CFT Excl. GST)',
                          dataIndex: 'rate',
                          key: 'rate',
                          render: (r) => `₹ ${Number(r).toFixed(4)}`,
                        },
                        {
                          title: 'Total (₹)',
                          dataIndex: 'amount',
                          key: 'amount',
                          render: (amt) => `₹ ${Number(amt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                        },
                      ]}
                      locale={{ emptyText: 'No purchase bill recorded' }}
                    />
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TimberInventory;
